import {
  Badge,
  Button,
  Checkbox,
  CheckboxGroup,
  Divider,
  HStack,
  Icon,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Text,
  useCheckboxGroup,
  useDisclosure,
} from '@chakra-ui/react';
import { FilterIcon } from '@heroicons/react/outline';
import { ActionMeta, AsyncSelect, MultiValue } from 'chakra-react-select';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslations } from 'use-intl';

import {
  SubscriptionFilterType,
  SubscriptionsFilterInput,
  useGetFundraisingQuery,
  useGetTagsLazyQuery,
  useGetTagsQuery,
} from '@blockpulse3/graphql/hooks';
import { ResponsiveModal, ResponsiveModalFooter } from '@blockpulse3/ui/commons';

import {
  TagInput,
  TagMultiValue,
} from '../SubscriptionDetailsSidePanel/SubscriptionDetailsTags/components';
import { TagOption } from '../SubscriptionDetailsSidePanel/SubscriptionDetailsTags/types';
import {
  getSubscriptionFilterHoldingMethod,
  getSubscriptionFilterStep,
  getSubscriptionFilterTags,
  subscriptionFilterIdentity,
} from './utils';

type Props = {
  /* ** Default checked filters ** */
  defaultValue: SubscriptionsFilterInput[];
  /* ** Callback on filter submit ** */
  onSubmit: (filters: SubscriptionsFilterInput[]) => void;
};

/**
 * FundraisingSubscribersTableFilters.
 * Renders filter button with modal of available checkboxes and tag select filters.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function FundraisingSubscribersTableFilters({
  defaultValue = [],
  onSubmit,
}: Props): JSX.Element {
  const i18nGeneral = useTranslations('General');
  const i18nTableFilters = useTranslations('FundraisingSubscribersTableFilters');
  const i18nTableFiltersKeys = useTranslations('FundraisingSubscribersTableFilters.Keys');

  const { companyId = '', fundraisingId = '' } = useParams();

  /* ** Filter modal disclosure ** */
  const { isOpen, onClose, onToggle } = useDisclosure();

  /* ** Fetch on inputChange ** */
  const [fetchTags] = useGetTagsLazyQuery();

  /* ** Checkbox group, holding all filters ** */
  const checkboxGroup = useCheckboxGroup();
  const checkboxFilters = defaultValue.map((filter) => filter.value || '');

  /* ** Build filter inputs for the submit and modal display ** */
  const fundraisingReq = useGetFundraisingQuery({ variables: { fundraisingId } });
  const fundraising = fundraisingReq.data?.fundraising;

  const subscriptionFiltersStep = getSubscriptionFilterStep(fundraising);
  const subscriptionFiltersTags = getSubscriptionFilterTags(fundraising);
  const subscriptionFiltersHoldingMethod = getSubscriptionFilterHoldingMethod(fundraising);

  /* ** Fetch available company tags to read their color ** */
  const companyTagsReq = useGetTagsQuery({
    variables: {
      getTagsInput: { companyId: companyId || fundraising?.company?.id || '', fundraisingId },
    },
    skip: !companyId && !fundraising?.company?.id,
  });
  const companyTags = companyTagsReq.data?.getTags || [];

  /* ** Tag filters sub group ** */
  const tagFilters = defaultValue
    .filter((filter) => filter.type === SubscriptionFilterType.TAGS)
    .map((filter) => filter.value || '');

  /* ** Tag filters as `<AsyncSelect />` options ** */
  const tagFilterOptions = companyTags
    .filter((tag) => tagFilters.includes(tag.id))
    .map((tag) => ({ label: tag.name, color: tag.color, value: tag.id }));

  /* ** Promise callback to load tags on input change ** */
  const loadTagOptions = useCallback(
    async (inputValue: string) => {
      const req = await fetchTags({
        variables: {
          getTagsInput: { companyId: companyId || fundraising?.company?.id || '', fundraisingId },
        },
        fetchPolicy: 'cache-and-network',
      });
      const companyTags = req.data?.getTags || [];

      return companyTags
        .filter((tag) => tag.name.toLowerCase().includes(inputValue.toLowerCase()))
        .map((tag) => ({ label: tag.name, value: tag.id, color: tag.color }));
    },
    [fetchTags, fundraisingId, companyId, fundraising?.company?.id],
  );

  const getFilterLabel = (name: string): string => {
    return i18nTableFiltersKeys(
      name as keyof IntlMessages['FundraisingSubscribersTableFilters']['Keys'],
    );
  };

  const getNoOptionsMessage = (): string => {
    return i18nTableFilters('SelectNoOptions');
  };

  /*
   * All current filters are saved in `checkboxGroup`. This method simulate checkbox triggers for
   * tag changes.
   * This avoids separate logic for tags.
   */
  const handleTagChange = (_: MultiValue<TagOption>, action: ActionMeta<TagOption>): void => {
    switch (action.action) {
      case 'select-option': {
        checkboxGroup.setValue((prevFilters) => [...prevFilters, action.option?.value || '']);
        break;
      }
      case 'pop-value':
      case 'remove-value': {
        if (action.removedValue) {
          checkboxGroup.setValue((prevFilter) =>
            prevFilter.filter((f) => f !== action.removedValue.value),
          );
        }
        break;
      }
      default: {
        break;
      }
    }
  };

  const handleFilterModalClose = (): void => {
    checkboxGroup.setValue(checkboxFilters);
    onClose();
  };

  const handleFilterModalSubmit = (): void => {
    const allFilters = [
      ...subscriptionFiltersStep,
      ...subscriptionFiltersHoldingMethod,
      ...subscriptionFilterIdentity,
      ...subscriptionFiltersTags,
    ];
    /* ** Transform `(string | number)[]` checkbox value into `SubscriptionsFilterInput[]` ** */
    const filters = allFilters.filter((option) => checkboxGroup.value.includes(option.value || ''));
    onSubmit(filters);
    onClose();
  };

  return (
    <>
      <Button
        position="relative"
        rightIcon={<Icon as={FilterIcon} />}
        variant="secondary"
        onClick={onToggle}
      >
        {i18nTableFilters('FilterTitle')}
        <Badge
          bg="secondary"
          borderRadius="full"
          color="white"
          fontSize="xs"
          hidden={checkboxGroup.value.length === 0}
          paddingX="1"
          position="absolute"
          right="6px"
          top="4px"
        >
          {checkboxGroup.value.length}
        </Badge>
      </Button>
      <ResponsiveModal isOpen={isOpen} size="xl" onClose={handleFilterModalClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{i18nTableFilters('Title')}</ModalHeader>
          <ModalBody>
            <CheckboxGroup defaultValue={checkboxFilters}>
              <Stack spacing="4">
                <Stack spacing="2">
                  <Text color="gray.600" fontSize="sm" fontWeight="500">
                    {i18nTableFilters('StepTitle')}
                  </Text>
                  <Divider />
                  <SimpleGrid gap="4" minChildWidth="250px" pb="4" pt="2">
                    {subscriptionFiltersStep.map((filter) => (
                      <Checkbox
                        key={filter.name}
                        alignItems="flex-start"
                        as={HStack}
                        lineHeight="1"
                        spacing="1"
                        {...checkboxGroup.getCheckboxProps({ value: filter.value })}
                      >
                        {getFilterLabel(filter.name)}
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                </Stack>
                <Stack spacing="2">
                  <Text color="gray.600" fontSize="sm" fontWeight="500">
                    {i18nTableFilters('HoldingMethodTitle')}
                  </Text>
                  <Divider />
                  <SimpleGrid gap="4" minChildWidth="250px" pb="4" pt="2">
                    {subscriptionFiltersHoldingMethod.map((filter) => {
                      return (
                        <Checkbox
                          key={filter.name}
                          alignItems="flex-start"
                          as={HStack}
                          lineHeight="1"
                          spacing="1"
                          {...checkboxGroup.getCheckboxProps({ value: filter.value })}
                        >
                          {getFilterLabel(filter.name)}
                        </Checkbox>
                      );
                    })}
                  </SimpleGrid>
                </Stack>
                <Stack spacing="2">
                  <Text color="gray.600" fontSize="sm" fontWeight="500">
                    {i18nTableFilters('IdentityTitle')}
                  </Text>
                  <Divider />
                  <SimpleGrid gap="4" minChildWidth="250px" pb="4" pt="2">
                    {subscriptionFilterIdentity.map((filter) => (
                      <Checkbox
                        key={filter.name}
                        alignItems="flex-start"
                        as={HStack}
                        lineHeight="1"
                        spacing="1"
                        {...checkboxGroup.getCheckboxProps({ value: filter.value })}
                      >
                        {getFilterLabel(filter.name)}
                      </Checkbox>
                    ))}
                  </SimpleGrid>
                </Stack>
                <Stack spacing="2">
                  <Text color="gray.600" fontSize="sm" fontWeight="500">
                    {i18nTableFilters('TagTitle')}
                  </Text>
                  <Divider />
                  <AsyncSelect
                    defaultOptions
                    isMulti
                    isSearchable
                    components={{ MultiValue: TagMultiValue, Input: TagInput }}
                    defaultValue={tagFilterOptions}
                    isClearable={false}
                    loadOptions={loadTagOptions}
                    menuPlacement="top"
                    noOptionsMessage={getNoOptionsMessage}
                    placeholder={i18nTableFilters('SelectPlaceholder')}
                    formatOptionLabel={(option: TagOption): JSX.Element => (
                      <Badge colorScheme={option.color}>{option.label}</Badge>
                    )}
                    onChange={handleTagChange}
                  />
                </Stack>
              </Stack>
            </CheckboxGroup>
          </ModalBody>
          <Divider />
          <ResponsiveModalFooter>
            <Button type="button" variant="secondary" onClick={handleFilterModalClose}>
              {i18nGeneral('Cancel')}
            </Button>
            <Button form="share-preference" type="submit" onClick={handleFilterModalSubmit}>
              {i18nGeneral('Submit')}
            </Button>
          </ResponsiveModalFooter>
        </ModalContent>
      </ResponsiveModal>
    </>
  );
}

export type FundraisingSubscribersTableFiltersProps = Props;
